"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { budgetTabs, musicTabs, randoTabs, lightTabs, voitureTabs,
  veloTabs, disposTabs, santeTabs, type NavItem } from "@/app/lib/navigation";
import type { ModuleFlags } from "./app-shell";
import { NotificationsBell } from "./notifications-bell";
import { useAccessibleModules, type AppModule } from "@/lib/auth/use-user-plan";

interface BottomTabBarProps {
  activeModule: "budget" | "music" | "famille" | "rando" | "light" | "voiture" | "velo" | "dispos" | "sante";
  moduleFlags?: ModuleFlags;
}

/**
 * Refonte UX nav mobile : au lieu d'une barre d'onglets serrée à scroll
 * horizontal (peu lisible, tap-zones étroites), une PASTILLE flottante en bas
 * à gauche (burger + page courante) + la cloche de notifications. Au tap, une
 * feuille glisse du bas avec : changement de module (pastilles) + les onglets
 * du module en grille 2 colonnes (grandes tap-zones, actif surligné).
 */

const MODULES: { key: AppModule; label: string; href: string; emoji: string }[] = [
  { key: "budget", label: "Budget", href: "/budget", emoji: "💶" },
  { key: "music", label: "Music", href: "/music", emoji: "🎵" },
  { key: "rando", label: "Rando", href: "/rando", emoji: "🏔️" },
  { key: "voiture", label: "Voiture", href: "/voiture", emoji: "🚗" },
  { key: "light", label: "Light", href: "/light", emoji: "✨" },
  { key: "famille", label: "Famille", href: "/famille", emoji: "🌳" },
  { key: "velo", label: "Vélo", href: "/velo", emoji: "🚲" },
  { key: "dispos", label: "Dispos", href: "/dispos", emoji: "🗓️" },
  { key: "sante", label: "Santé", href: "/sante", emoji: "🩺" },
];

export function BottomTabBar({
  activeModule,
  moduleFlags = { intermittenceEnabled: false, portefeuilleEnabled: false, groupeAmisEnabled: false, groupeAmisLabel: "Groupe d'amis" },
}: BottomTabBarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const accessible = useAccessibleModules();
  const [open, setOpen] = useState(false);
  // Gestes sur la pastille : tap (ouvre la feuille), swipe ←/→ (onglet
  // préc/suiv), et APPUI LONG → la feuille s'ouvre en mode sélection : on
  // garde le pouce, on glisse sur l'onglet voulu, on relâche → navigation
  // éclair (la feuille disparaît).
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [dragSelect, setDragSelect] = useState(false);
  const [hoverHref, setHoverHref] = useState<string | null>(null);
  const dragRef = useRef<{ x: number; y: number; horiz: boolean | null; swiped: boolean; dx: number; lp: boolean; hover: string | null }>({ x: 0, y: 0, horiz: null, swiped: false, dx: 0, lp: false, hover: null });
  const lpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Famille = page full-bleed avec ses propres boutons en bas → on n'ajoute
  // pas la pastille pour ne pas faire double emploi.
  const isFamille = activeModule === "famille";

  let allTabs: NavItem[] = activeModule === "budget" ? budgetTabs
    : activeModule === "music" ? musicTabs
    : activeModule === "rando" ? randoTabs
    : activeModule === "light" ? lightTabs
    : activeModule === "voiture" ? voitureTabs
    : activeModule === "velo" ? veloTabs
    : activeModule === "dispos" ? disposTabs
    : activeModule === "sante" ? santeTabs
    : [];

  if (activeModule === "budget") {
    const { intermittenceEnabled, portefeuilleEnabled, groupeAmisEnabled, groupeAmisLabel } = moduleFlags;
    allTabs = allTabs.filter((t) => {
      if (t.href === "/budget/intermittence") return intermittenceEnabled;
      if (t.href === "/budget/portefeuille") return portefeuilleEnabled;
      if (t.optionalModule === "groupeAmis") return groupeAmisEnabled;
      return true;
    });
    if (groupeAmisEnabled && groupeAmisLabel) {
      allTabs = allTabs.map((t) => (t.optionalModule === "groupeAmis" ? { ...t, label: groupeAmisLabel } : t));
    }
  }

  // Light : même réglage Groupe d'amis que Budget Pro (toggle Réglages Light).
  if (activeModule === "light") {
    const { groupeAmisEnabled, groupeAmisLabel } = moduleFlags;
    allTabs = allTabs.filter((t) => t.href !== "/light/amis" || groupeAmisEnabled);
    allTabs = allTabs.filter((t) => t.href !== "/light/repartition" || !!moduleFlags.repartitionEnabled);
    if (groupeAmisEnabled && groupeAmisLabel) {
      allTabs = allTabs.map((t) => (t.href === "/light/amis" ? { ...t, label: groupeAmisLabel } : t));
    }
  }

  // Accent par module.
  const accent = activeModule === "budget" || activeModule === "light" ? "forest"
    : activeModule === "rando" ? "emerald"
    : "terra";
  const accentText = accent === "forest" ? "text-forest" : accent === "emerald" ? "text-emerald-600 dark:text-emerald-400" : "text-terra";
  const accentBg = accent === "forest" ? "bg-forest" : accent === "emerald" ? "bg-emerald-600" : "bg-terra";
  const accentSoft = accent === "forest" ? "bg-forest/12 dark:bg-forest/20 text-forest" : accent === "emerald" ? "bg-emerald-500/12 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300" : "bg-terra/12 dark:bg-terra/20 text-terra";

  function isActive(tab: NavItem) {
    if (tab.href === `/${activeModule}`) return pathname === tab.href;
    return pathname.startsWith(tab.href);
  }
  const activeTab = allTabs.find(isActive);

  const modules = MODULES.filter((m) => accessible.has(m.key));
  const canSwitch = modules.length > 1;

  // ── Swipe sur la pastille : onglet précédent / suivant ──────────────
  const curIdx = allTabs.findIndex(isActive);
  const nextTab = curIdx >= 0 && curIdx < allTabs.length - 1 ? allTabs[curIdx + 1] : null;
  const prevTab = curIdx > 0 ? allTabs[curIdx - 1] : null;
  // Précharge les onglets voisins → swipe instantané (prefetch = prod only).
  useEffect(() => {
    if (nextTab) router.prefetch(nextTab.href);
    if (prevTab) router.prefetch(prevTab.href);
  }, [router, nextTab?.href, prevTab?.href]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pendant la sélection (appui long + glisse) : couper la sélection de texte
  // de la page (sinon glisser le pouce surligne le texte partout) + callout iOS.
  useEffect(() => {
    if (!dragSelect) return;
    const b = document.body.style;
    b.setProperty("user-select", "none");
    b.setProperty("-webkit-user-select", "none");
    b.setProperty("-webkit-touch-callout", "none");
    return () => {
      b.removeProperty("user-select");
      b.removeProperty("-webkit-user-select");
      b.removeProperty("-webkit-touch-callout");
    };
  }, [dragSelect]);

  const SWIPE_THRESHOLD = 45;
  const LONG_PRESS_MS = 320;

  /** href de l'onglet/module physiquement sous le doigt (data-navtab). */
  function tabHrefAt(x: number, y: number): string | null {
    const el = document.elementFromPoint(x, y);
    return el?.closest("[data-navtab]")?.getAttribute("data-navtab") ?? null;
  }
  function clearLp() { if (lpTimer.current) { clearTimeout(lpTimer.current); lpTimer.current = null; } }

  function onSwipeStart(e: React.TouchEvent) {
    const t = e.touches[0];
    dragRef.current = { x: t.clientX, y: t.clientY, horiz: null, swiped: false, dx: 0, lp: false, hover: null };
    setDragging(true);
    clearLp();
    // Appui long : si le doigt reste (≈immobile) LONG_PRESS_MS → mode sélection.
    lpTimer.current = setTimeout(() => {
      dragRef.current.lp = true;
      setDragSelect(true);
      setOpen(true);
      setDragX(0);
      try { navigator.vibrate?.(12); } catch { /* haptique web absente sur iOS */ }
    }, LONG_PRESS_MS);
  }
  function onSwipeMove(e: React.TouchEvent) {
    const d = dragRef.current;
    const t = e.touches[0];
    const dx = t.clientX - d.x;
    const dy = t.clientY - d.y;
    if (d.lp) {
      // Mode sélection : on n'ACTIVE la sélection qu'après un VRAI déplacement
      // depuis le point d'appui (≈26px). Sinon l'onglet qui se trouve sous le
      // pouce au repos (ou qui défile sous lui pendant l'anim d'ouverture de la
      // feuille) serait sélectionné sans qu'on ait bougé → relâcher naviguerait
      // vers le mauvais onglet. Tant qu'on n'a pas bougé : aucune sélection.
      const moved = Math.hypot(dx, dy) >= 26;
      const href = moved ? tabHrefAt(t.clientX, t.clientY) : null;
      if (href !== d.hover) {
        d.hover = href;
        setHoverHref(href);
        if (href) { try { navigator.vibrate?.(6); } catch { /* */ } }
      }
      return;
    }
    if (d.horiz === null) {
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        d.horiz = Math.abs(dx) > Math.abs(dy);
        clearLp(); // un vrai mouvement = ce n'est pas un appui long
      }
    }
    if (d.horiz !== true) return; // vertical → laisse défiler
    d.swiped = true;
    d.dx = dx;
    // dx < 0 (swipe gauche) → onglet suivant ; dx > 0 → précédent.
    const target = dx < 0 ? nextTab : prevTab;
    // Au bout (pas d'onglet dans ce sens) → effet élastique amorti (rebond).
    const eff = target ? dx : dx * 0.28;
    setDragX(Math.max(-90, Math.min(90, eff)));
  }
  function onSwipeEnd() {
    const d = dragRef.current;
    setDragging(false);
    clearLp();
    if (d.lp) {
      // Sélection éclair : on navigue vers l'onglet relâché, la feuille ferme.
      const href = d.hover;
      setDragSelect(false);
      setHoverHref(null);
      setOpen(false);
      if (href) router.push(href);
      return;
    }
    setDragX(0); // revient à 0 avec transition ressort (= rebond au relâché)
    if (d.horiz === true && d.swiped && Math.abs(d.dx) > SWIPE_THRESHOLD) {
      const target = d.dx < 0 ? nextTab : prevTab;
      if (target) router.push(target.href);
    }
  }
  function onSwipeCancel() {
    clearLp();
    setDragging(false);
    setDragX(0);
    if (dragRef.current.lp) {
      dragRef.current.lp = false;
      setDragSelect(false);
      setHoverHref(null);
      setOpen(false);
    }
  }
  function onPastilleClick() {
    // Appui long ou swipe déjà traité → ne pas (ré)ouvrir via le tap.
    if (dragRef.current.lp) { dragRef.current.lp = false; return; }
    if (dragRef.current.swiped) { dragRef.current.swiped = false; return; }
    setOpen(true);
  }

  if (isFamille) return null;

  return (
    <>
      {/* Pastille flottante : burger (page courante) + cloche notifs */}
      <div
        className="md:hidden fixed left-3 z-50 flex items-center gap-2"
        style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <button
          type="button"
          onClick={onPastilleClick}
          onTouchStart={onSwipeStart}
          onTouchMove={onSwipeMove}
          onTouchEnd={onSwipeEnd}
          onTouchCancel={onSwipeCancel}
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          onContextMenu={(e) => e.preventDefault()}
          aria-label="Navigation : tap pour ouvrir, glisser ←/→ pour changer d'onglet, appui long puis glisser pour choisir"
          className={`no-touch-drag flex items-center gap-2 h-12 pl-3.5 pr-4 rounded-2xl bg-surface/95 backdrop-blur-lg border shadow-lg transition-colors ${dragSelect ? "border-foreground/40" : "border-border-default"}`}
          style={{
            transform: `translateX(${dragX}px)`,
            transition: dragging ? "none" : "transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1)",
            touchAction: dragSelect ? "none" : "pan-y",
          }}
        >
          <svg className="w-5 h-5 text-foreground shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
          <span className="text-sm font-semibold text-foreground max-w-[40vw] truncate">{activeTab?.label ?? "Menu"}</span>
        </button>
        {/* Pas de backdrop-blur ici : `backdrop-filter` crée un bloc
            conteneur qui piégeait le panneau `fixed` des notifs (rendu en
            bande étroite de 32 px). Fond quasi-opaque → le flou ne manque pas. */}
        <span className="h-12 w-12 flex items-center justify-center rounded-2xl bg-surface border border-border-default shadow-lg">
          <NotificationsBell />
        </span>
      </div>

      {/* Feuille de navigation */}
      {open && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-[fadeIn_180ms_ease-out]" onClick={() => setOpen(false)} aria-hidden />
          <div
            className="no-touch-drag absolute left-0 right-0 bottom-0 rounded-t-3xl bg-surface border-t border-border-default shadow-2xl animate-lg-fade-up"
            style={{
              // En mode appui-long, on ajoute une grosse marge en bas pour
              // qu'AUCUN onglet ne se trouve sous le pouce au repos (zone de
              // départ vide) → rien n'est sélectionnable tant qu'on n'a pas
              // glissé vers le haut sur un onglet.
              paddingBottom: dragSelect
                ? "calc(4.5rem + env(safe-area-inset-bottom, 0px))"
                : "calc(1rem + env(safe-area-inset-bottom, 0px))",
            }}
          >
            <div className="flex justify-center pt-2.5 pb-1"><span className="h-1.5 w-10 rounded-full bg-border-default" aria-hidden /></div>
            {dragSelect && (
              <p className="text-center text-[11px] text-text-muted px-4 pb-1.5 select-none">Glisse vers un onglet, relâche pour y aller</p>
            )}

            {/* Changement de module */}
            {canSwitch && (
              <div className="px-4 pt-1 pb-3 overflow-x-auto no-scrollbar">
                <div className="flex gap-2 w-max">
                  {modules.map((m) => {
                    const cur = m.key === activeModule;
                    return (
                      <Link
                        key={m.key}
                        href={m.href}
                        prefetch
                        draggable={false}
                        data-navtab={m.href}
                        onClick={() => setOpen(false)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium shrink-0 transition-all ${dragSelect && hoverHref === m.href ? `${accentSoft} ring-2 ring-foreground/40 scale-105` : cur ? accentSoft : "bg-surface-secondary/60 text-text-secondary"}`}
                      >
                        <span>{m.emoji}</span>{m.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Onglets du module en grille */}
            {allTabs.length > 0 && (
              <div className="px-4 pb-4 grid grid-cols-2 gap-2">
                {allTabs.map((tab) => {
                  const active = isActive(tab);
                  return (
                    <Link
                      key={tab.href}
                      href={tab.href}
                      prefetch
                      draggable={false}
                      data-navtab={tab.href}
                      onClick={() => setOpen(false)}
                      className={`flex items-center gap-3 px-3.5 py-3 rounded-2xl border text-left transition-all ${dragSelect && hoverHref === tab.href ? `${accentSoft} border-transparent ring-2 ring-foreground/40 scale-[1.03]` : active ? `${accentSoft} border-transparent` : "bg-surface-secondary/40 border-border-default/40 text-text-secondary active:bg-surface-secondary"}`}
                    >
                      <svg className={`w-6 h-6 shrink-0 ${active ? "" : accentText} ${active ? "" : "opacity-70"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={active ? 2.1 : 1.7}>
                        <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
                      </svg>
                      <span className={`text-sm ${active ? "font-bold" : "font-medium"} truncate`}>{tab.label}</span>
                      {tab.subModule && <span className="ml-auto h-2 w-2 rounded-full bg-blue-500 shrink-0" aria-hidden />}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
